/**
 * parseGoogleAddress
 *
 * Extracts structured address components from a Google Place object returned
 * by place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] }).
 *
 * Uses the NEW Places JS API property names (camelCase):
 *   place.addressComponents     (not address_components)
 *   component.longText          (not long_name)
 *   component.shortText         (not short_name)
 *
 * Component mapping (country-neutral; the labels are the tenant's):
 *   street_number + route                        → street
 *   locality / postal_town / sublocality_level_1 → suburb (locality)
 *   administrative_area_level_1                  → state (region; shortText = VIC, TX, ON, …)
 *   postal_code                                  → postcode
 *
 * Returns isPartial: true if any of suburb/state/postcode is missing.
 */

export interface NormalizedAddress {
  /** Street number + street name only. e.g., "42 King Street" */
  street: string;
  /** Locality: suburb / city / town. e.g., "Melbourne" */
  suburb: string;
  /** Region abbreviation. e.g., "VIC", "TX", "ON" */
  state: string;
  /** Postal code. e.g., "3000", "78701", "SW1A 1AA" */
  postcode: string;
  /** Full formatted address from Google, used as the raw input value */
  formattedAddress: string;
  /**
   * True if one or more of suburb/state/postcode could not be extracted.
   * Parent forms must NOT overwrite existing non-empty fields with empty strings
   * when isPartial is true.
   */
  isPartial: boolean;
  /** ISO 3166-1 alpha-2 country of the selected place ("" when Google omits it). */
  countryCode: string;
  /** @deprecated use `countryCode`; true when the place is outside Australia. */
  isNonAU: boolean;
  /** WGS-84 latitude from place.location, null if not available */
  lat: number | null;
  /** WGS-84 longitude from place.location, null if not available */
  lng: number | null;
}

export function parseGoogleAddress(place: google.maps.places.Place): NormalizedAddress {
  // New Places JS API uses camelCase: place.addressComponents
  const components = place.addressComponents ?? [];

  const formattedAddress = place.formattedAddress ?? '';

  // Helper to find address_component by type using new API property names
  const get = (type: string, field: 'longText' | 'shortText' = 'longText'): string => {
    const comp = components.find((c) => c.types.includes(type));
    return comp?.[field] ?? '';
  };

  const streetNumber = get('street_number');
  const route = get('route');
  const street = [streetNumber, route].filter(Boolean).join(' ');

  // UK addresses carry the town in postal_town rather than locality.
  const suburb =
    get('locality') ||
    get('postal_town') ||
    get('sublocality_level_1') ||
    get('administrative_area_level_2');

  // Region: shortText gives the abbreviation people expect (VIC, TX, ON, "England").
  const stateRaw = get('administrative_area_level_1', 'shortText');
  const state = stateRaw.length <= 3 ? stateRaw.toUpperCase() : stateRaw;

  const postcode = get('postal_code');

  const countryCode = get('country', 'shortText').toUpperCase();
  const isNonAU = countryCode !== '' && countryCode !== 'AU';

  const isPartial = !suburb || !state || !postcode;

  const lat = place.location?.lat() ?? null;
  const lng = place.location?.lng() ?? null;

  return {
    street: street || formattedAddress, // fall back to full address if components missing
    suburb,
    state,
    postcode,
    formattedAddress,
    isPartial,
    countryCode,
    isNonAU,
    lat,
    lng,
  };
}
